import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useSelectedOrganization } from "@/hooks/useCurrentUser";
import type { Brand } from "@/hooks/useGetBrands";

/** Rename a brand (PATCH /brand/:id). Optimistic on the org's brand list. */
export function useRenameBrand() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useSelectedOrganization();
  const key = ["brands", selectedOrganization?.organization.id ?? null];

  return useMutation({
    mutationFn: async ({ brandId, name }: { brandId: string; name: string }) => {
      await axiosInstance.patch(`/brand/${brandId}`, { name });
    },
    onMutate: async ({ brandId, name }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Brand[]>(key)?.find((b) => b.id === brandId)?.name ?? null;
      queryClient.setQueryData<Brand[]>(key, (old) => old?.map((b) => (b.id === brandId ? { ...b, name } : b)));
      return { previous };
    },
    onSuccess: (_data, { name }) => {
      queue.add({ title: `Brand renamed to ${name}`, variant: "success" });
    },
    onError: (_err, { brandId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData<Brand[]>(key, (old) =>
          old?.map((b) => (b.id === brandId ? { ...b, name: context.previous as string } : b)),
        );
      }
      queue.add({ title: "Could not rename the brand", description: "Please try again.", variant: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
